import { useEffect, useState } from 'react';
import { api } from '../services/api';

interface PegPairData {
  total_checks: number;
  compliant_checks: number;
  failed_checks: number;
  compliance_pct: number;
  min_value: number;
  max_value: number;
  avg_pct: number;
  min_pct: number;
  max_pct: number;
  minimum: number;
}

interface PegLatest {
  current: number | null;
  percentage: number | null;
  compliant: boolean;
  time: string;
}

interface PegSummary {
  pairs: Record<string, PegPairData>;
  latest: Record<string, PegLatest>;
  overall_compliance_pct: number;
  total_checks: number;
  hours: number;
}

export function PegStatus({ hours = 24 }: { hours?: number }) {
  const [summary, setSummary] = useState<PegSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [hours]);

  const loadData = async () => {
    try {
      const data = await api.getPegSummary(hours);
      setSummary(data);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  if (loading) return <div className="peg-status loading">Loading peg status...</div>;
  if (!summary || Object.keys(summary.pairs).length === 0) {
    return <div className="peg-status empty">No peg monitoring data yet</div>;
  }

  const overallPct = summary.overall_compliance_pct;
  const badgeClass = overallPct >= 100 ? 'good' : overallPct >= 80 ? 'warn' : 'bad';

  return (
    <div className="peg-status">
      <div className="peg-header">
        <h3>Peg Compliance</h3>
        <span className={`stat-badge ${badgeClass}`}>{overallPct}%</span>
      </div>
      <div className="peg-pairs">
        {Object.entries(summary.latest).map(([pair, latest]) => {
          const history = summary.pairs[pair];
          const pctOfMin = latest.percentage ?? 0;
          const isOk = latest.compliant;
          const barWidth = Math.min(pctOfMin, 200) / 2; // scale to 0-100 width

          return (
            <div key={pair} className={`peg-pair-row ${isOk ? 'compliant' : 'violation'}`}>
              <div className="peg-pair-name">{pair}</div>
              <div className="peg-pair-bar-container">
                <div
                  className={`peg-pair-bar ${isOk ? 'bar-ok' : 'bar-fail'}`}
                  style={{ width: `${barWidth}%` }}
                />
                <div className="peg-pair-threshold" style={{ left: '50%' }} />
              </div>
              <div className="peg-pair-value">
                <span className={isOk ? 'positive' : 'negative'}>{pctOfMin.toFixed(0)}%</span>
                {history && (
                  <span className="peg-pair-compliance" title={`${history.compliant_checks}/${history.total_checks} checks passed`}>
                    {history.compliance_pct}% 24h
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
